import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Briefcase, Upload, MessageCircle } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/sections/CTASection";
import { CAREERS, CULTURE_PILLARS, BENEFITS } from "@/data/careers";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { buildWhatsAppLink } from "@/data/site";

const HERO_IMG = "https://images.pexels.com/photos/8961134/pexels-photo-8961134.jpeg?w=2400";

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    const text = `Hello NEPHRC,

I'd like to apply for a role.

Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone}
Role: ${form.role || "Open"}

Message:
${form.message || "—"}`;
    window.open(buildWhatsAppLink(text), "_blank");
    toast.success("Opening WhatsApp — please attach your resume in the chat.");
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        overline="Careers"
        title={<>Build a career that<br /><span className="italic font-light text-gold">builds India.</span></>}
        subtitle="Join engineers, architects and consultants who get to lead landmark projects across infrastructure, hospitality and retail."
        image={HERO_IMG}
        breadcrumbs={[{ label: "Careers" }]}
      />

      <section className="bg-white py-24">
        <div className="container-x">
          <div className="overline text-emerald-brand">Our culture</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-2 leading-tight">
            How we work.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {CULTURE_PILLARS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white p-7"
              >
                <div className="font-mono-alt text-xs text-emerald-brand">0{i + 1}.</div>
                <div className="mt-4 font-display font-bold text-lg text-navy">{c.title}</div>
                <p className="mt-3 text-sm text-slate-brand leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-mist py-24">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-emerald-brand">Benefits</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-2 leading-tight">
              We invest in our people.
            </h2>
            <ul className="mt-8 space-y-3">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-brand">
                  <span className="w-1.5 h-1.5 mt-2 bg-gold" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="overline text-emerald-brand">Current openings</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-2 leading-tight">
              We&apos;re hiring across disciplines.
            </h2>
            <div className="mt-8 space-y-3">
              {CAREERS.map((j, i) => (
                <motion.div
                  key={j.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="bg-white p-6 border-l-2 border-gold"
                  data-testid={`career-card-${j.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div>
                      <h3 className="font-display font-bold text-lg text-navy">{j.title}</h3>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-brand">
                        <span className="flex items-center gap-1"><MapPin size={12} className="text-emerald-brand" /> {j.location}</span>
                        <span className="flex items-center gap-1"><Briefcase size={12} className="text-emerald-brand" /> {j.type}</span>
                        <span>{j.experience}</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-brand leading-relaxed">{j.description}</p>
                    </div>
                    <a
                      href={buildWhatsAppLink(`Hi NEPHRC, I'd like to apply for the ${j.title} role in ${j.location}.`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`career-apply-${j.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-navy text-white text-xs tracking-[0.2em] uppercase font-semibold hover:bg-emerald-brand transition-colors"
                    >
                      <MessageCircle size={14} /> Apply
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32" id="apply">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-emerald-brand">Open application</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-2 leading-[1.05]">
              Don&apos;t see your role?<br />Apply anyway.
            </h2>
            <p className="mt-6 text-slate-brand leading-relaxed">
              Send us your details below. We&apos;ll pre-open a WhatsApp message — attach your resume in the chat and our team will get back to you within 3 working days.
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm text-slate-brand">
              <Upload size={16} className="text-emerald-brand" />
              Attach PDF / DOC resume in the WhatsApp chat
            </div>
          </div>
          <form onSubmit={submit} className="lg:col-span-7 bg-mist p-8 sm:p-10 space-y-5" data-testid="careers-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline text-emerald-brand block mb-2">Full Name *</label>
                <Input data-testid="career-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-300" />
              </div>
              <div>
                <label className="overline text-emerald-brand block mb-2">Phone *</label>
                <Input data-testid="career-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-slate-300" />
              </div>
              <div>
                <label className="overline text-emerald-brand block mb-2">Email</label>
                <Input data-testid="career-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-slate-300" />
              </div>
              <div>
                <label className="overline text-emerald-brand block mb-2">Role of Interest</label>
                <Input data-testid="career-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-white border-slate-300" />
              </div>
            </div>
            <div>
              <label className="overline text-emerald-brand block mb-2">Message</label>
              <Textarea data-testid="career-form-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white border-slate-300" />
            </div>
            <button
              type="submit"
              data-testid="career-form-submit"
              className="inline-flex items-center gap-2 px-6 py-4 bg-gold text-navy font-semibold text-xs tracking-[0.2em] uppercase hover:bg-[#E8C45F] transition-colors"
            >
              <MessageCircle size={14} /> Send via WhatsApp
            </button>
          </form>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
